import React from "react";
import { FaChevronDown } from "react-icons/fa6";

export type SelectOption = {
  value: string;
  label: string;
};

/** Layout: full-width dashboard rows vs compact inline (e.g. small toolbar controls). */
export type SelectVariant = "default" | "compact";

/**
 * Standard sizes match `Button` heights.
 * Backward compatibility:
 * - `default` maps to `md`
 * - `form` maps to `lg`
 */
export type SelectSize = "sm" | "md" | "lg" | "default" | "form";

export type SelectProps = {
  label?: string;
  placeholder?: string;
  value?: string;
  options: SelectOption[];
  onChange?: (value: string) => void;
  disabled?: boolean;
  variant?: SelectVariant;
  size?: SelectSize;
  /** Extra classes merged onto the native &lt;select&gt; (same idea as `Button` `className`). */
  className?: string;
  /** Use when there is no visible `label` (e.g. placeholder-only selects). */
  ariaLabel?: string;
};

const baseClasses = "w-full appearance-none bg-white outline-none";

const sizeClasses: Record<SelectSize, string> = {
  sm: "h-9 rounded-[8px] border border-[#E1E1E1] px-3 pr-9 text-sm text-black",
  md: "h-10 rounded-[8px] border border-[#E1E1E1] px-4 pr-10 text-md text-black",
  lg: "h-[50px] rounded-[8px] border border-[#E1E1E1] px-4 pr-10 text-lg leading-normal text-black",
  default: "h-10 rounded-[8px] border border-[#E1E1E1] px-4 pr-10 text-md text-black",
  form: "h-[50px] rounded-[8px] border border-[#E1E1E1] px-4 pr-10 text-lg leading-normal text-black",
};

export default function Select({
  label,
  placeholder,
  value,
  options,
  onChange,
  disabled,
  variant = "default",
  size = "md",
  className = "",
  ariaLabel,
}: SelectProps) {
  const generatedId = React.useId();
  const selectedValue = value ?? options?.[0]?.value ?? "";

  const selectId = generatedId;
  const labelId = `${generatedId}-label`;

  const fieldClasses = [
    sizeClasses[size],
    "transition-colors duration-200 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue",
    variant === "compact" ? "min-w-[72px]" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const selectClasses = [
    baseClasses,
    fieldClasses,
    disabled ? "cursor-not-allowed opacity-60" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const chevronClasses = variant === "compact" ? "right-2.5" : "right-3";

  const selectEl = (
    <div className={variant === "default" ? "relative" : "relative inline-flex"}>
      <select
        aria-labelledby={label ? labelId : undefined}
        aria-label={!label ? ariaLabel : undefined}
        id={selectId}
        value={selectedValue}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.value)}
        className={selectClasses}
      >
        {placeholder ? (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <FaChevronDown
        size={12}
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-dark-Grey ${chevronClasses}`}
        aria-hidden="true"
      />
    </div>
  );

  if (variant === "compact" || !label) return selectEl;

  return (
    <label className="block space-y-2 text-sm font-medium text-dark-black">
      <span id={labelId}>{label}</span>
      {selectEl}
    </label>
  );
}
